import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const SLIDES = [
  "https://customer-assets.emergentagent.com/job_charge-charlie-band/artifacts/ebrd2hxj_kep_slider_1.webp",
  "https://customer-assets.emergentagent.com/job_charge-charlie-band/artifacts/mpr8f3u3_kep_slider_2.webp",
  "https://customer-assets.emergentagent.com/job_charge-charlie-band/artifacts/k14namcz_kep_slider_3.webp",
  "https://customer-assets.emergentagent.com/job_charge-charlie-band/artifacts/x2iizpy1_kep_slider_4.webp",
  "https://customer-assets.emergentagent.com/job_charge-charlie-band/artifacts/9dn2f0x6_kep_slider_5.webp",
];

const INTERVAL_MS = 3000;

export const Gallery = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const timerRef = useRef(null);

  // Auto-advance
  useEffect(() => {
    if (paused || lightboxOpen) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, lightboxOpen]);

  const goTo = (i) => setIndex((i + SLIDES.length) % SLIDES.length);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  const openLightbox = (i) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  const lightboxNext = () =>
    setLightboxIndex((i) => (i + 1) % SLIDES.length);
  const lightboxPrev = () =>
    setLightboxIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length);

  return (
    <section
      id="kepek"
      className="relative w-full py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#050505]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <p className="font-display text-xs tracking-[0.4em] uppercase text-[#00E5FF] mb-6">
              // 04 — Pillanatok
            </p>
            <h2 className="font-display font-bold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl">
              Képek
            </h2>
          </div>
          <p className="font-body text-white/50 max-w-sm mt-6 md:mt-0 md:text-right">
            Pillanatok a színpadról.
          </p>
        </div>

        {/* Slider */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="gallery-slider"
        >
          {/* Glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-[#00E5FF] via-[#7000FF] to-[#FF007F] opacity-20 blur-xl pointer-events-none" />

          <div className="relative aspect-video bg-black overflow-hidden border border-white/10">
            {SLIDES.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => openLightbox(i)}
                aria-label={`Kép ${i + 1} megnyitása`}
                data-testid={`slider-image-${i}`}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  i === index ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
                tabIndex={i === index ? 0 : -1}
              >
                <img
                  src={src}
                  alt={`Charge koncert pillanat ${i + 1}`}
                  className="absolute inset-0 h-full w-full object-cover cursor-zoom-in"
                  loading={i === 0 ? "eager" : "lazy"}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/40 via-transparent to-transparent pointer-events-none" />
              </button>
            ))}

            {/* Prev / Next */}
            <button
              type="button"
              onClick={prev}
              aria-label="Előző kép"
              data-testid="slider-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex h-12 w-12 items-center justify-center bg-black/50 border border-white/20 text-white hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-all"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Következő kép"
              data-testid="slider-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex h-12 w-12 items-center justify-center bg-black/50 border border-white/20 text-white hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-all"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* Counter */}
            <div className="absolute top-4 right-4 z-20 px-3 py-1.5 bg-black/60 border border-white/15 font-display text-xs tracking-[0.3em] text-white">
              {String(index + 1).padStart(2, "0")} /{" "}
              {String(SLIDES.length).padStart(2, "0")}
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-3 mt-6">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Ugrás a(z) ${i + 1}. képre`}
                data-testid={`slider-dot-${i}`}
                className={`h-1.5 transition-all duration-300 ${
                  i === index
                    ? "w-10 bg-[#00E5FF]"
                    : "w-6 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="bg-black/95 border-white/10 text-white max-w-[95vw] md:max-w-6xl p-0 overflow-hidden [&>button]:hidden"
          data-testid="lightbox-dialog"
        >
          <div className="relative aspect-video bg-black">
            <img
              src={SLIDES[lightboxIndex]}
              alt={`Charge koncert pillanat ${lightboxIndex + 1}`}
              className="absolute inset-0 h-full w-full object-contain"
            />
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              aria-label="Bezárás"
              data-testid="lightbox-close"
              className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[#FF007F] hover:border-[#FF007F] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={lightboxPrev}
              aria-label="Előző"
              data-testid="lightbox-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-all"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={lightboxNext}
              aria-label="Következő"
              data-testid="lightbox-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-all"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/60 border border-white/15 font-display text-xs tracking-[0.3em] text-white">
              {String(lightboxIndex + 1).padStart(2, "0")} /{" "}
              {String(SLIDES.length).padStart(2, "0")}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Gallery;
